import axios from 'axios';
import { createAuthenticatedApiClient, setAuthHeader } from '../config/api';

/**
 * Display Service
 * 
 * Provides methods for interacting with display-related API endpoints
 */

// Create authenticated API client for display operations
const apiClient = createAuthenticatedApiClient();

// Types for display data
export interface DisplayLocation {
  zipcode: string;
  city?: string;
  state?: string;
  coordinates?: {
    type: string;
    coordinates: number[];
  };
  label?: string;
}

export interface DisplayConfiguration {
  content?: {
    displayDuration?: number;
  };
  visual?: {
    colorScheme?: string;
    showQrCode?: boolean;
    qrCodePlacement?: string;
    headerText?: string;
    footerText?: string;
  };
  session?: {
    operatingHours?: {
      start?: string;
      end?: string;
      timezone?: string;
      activeDays?: string[];
    };
    refreshRate?: number;
    rotationPattern?: string;
  };
  schedule?: {
    schedules?: Array<{
      id: string;
      startTime: string;
      endTime: string;
      days: string[];
      active: boolean;
    }>;
  };
}

export interface DisplayAnalytics {
  totalNotesShown: number;
  totalQrScans: number;
  notesInspired: number;
  lastActiveDate: string;
}

export interface DisplayData {
  _id?: string;
  name: string;
  customName?: string;
  ownerId?: string;
  static_auth_token?: string;
  client_display_id?: string;
  location?: DisplayLocation;
  status?: 'active' | 'inactive' | 'pending';
  configuration?: DisplayConfiguration;
  analytics?: DisplayAnalytics;
  createdAt?: string;
  updatedAt?: string;
}

export interface DisplayFormData {
  name: string;
  customName?: string;
  location: {
    zipcode: string;
    label?: string;
  };
  clientId: string;
}

// Note type for display notes
export interface DisplayNote {
  _id: string;
  note: string;
  contributorInfo?: {
    firstName: string;
    lastName: string;
    aliasName?: string;
  };
  createdAt: string;
  updatedAt?: string;
  heart_count?: number;
  display?: {
    viewCount: number;
  };
}

// Session related interfaces
export interface DisplaySession {
  _id: string;
  displayId: string;
  status: 'active' | 'ended' | 'expired';
  startTime: string;
  endTime?: string;
  duration?: number; // in seconds
  clientInfo?: {
    userAgent?: string;
    screenWidth?: number;
    screenHeight?: number;
    ipAddress?: string;
    location?: {
      city?: string;
      state?: string;
      country?: string;
    };
  };
  noteViews?: number;
  lastPingTime?: string;
}

// Analytics data interface
export interface DisplayAnalyticsData {
  summary: {
    totalNotesShown: number;
    totalQrScans: number;
    totalSessions: number;
    notesInspired: number;
  };
  timeData: {
    labels: string[];
    noteViews: number[];
    qrScans: number[];
  };
  topNotes: Array<{
    noteId: string;
    noteContent: string;
    displayCount: number;
    scanCount: number;
  }>;
  geoDistribution?: Array<{
    region: string;
    sessionCount: number;
    viewCount: number;
    scanCount: number;
    coordinates?: [number, number]; // [longitude, latitude]
  }>;
}

// Response types for API calls
interface DisplaysResponse {
  displays: DisplayData[];
}

// Response types for notes operations
interface DisplayNotesResponse {
  notes: DisplayNote[];
  displayId: string;
}

// Response types for session operations
interface SessionsResponse {
  sessions: DisplaySession[];
  total: number;
}

interface SessionResponse {
  session: DisplaySession;
}

/**
 * Get all displays owned by the current user
 */
export const fetchDisplays = async (): Promise<DisplayData[]> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.get<DisplaysResponse>('/account/displays');
    
    // Safety check: ensure we return an array even if API response is malformed
    if (!response.data || !Array.isArray(response.data.displays)) {
      console.warn('fetchDisplays: API response does not contain displays array:', response.data);
      return [];
    }
    
    return response.data.displays;
  } catch (error) {
    console.error('Error fetching displays:', error);
    throw error;
  }
};

/**
 * Get a specific display by ID
 */
export const fetchDisplay = async (id: string): Promise<DisplayData> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.get<DisplayData>(`/account/displays/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching display ${id}:`, error);
    throw error;
  }
};

/**
 * Register a new display
 */
export const registerDisplay = async (displayData: DisplayFormData): Promise<DisplayData> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.post<DisplayData>('/account/displays/register', displayData);
    return response.data;
  } catch (error) {
    console.error('Error registering display:', error);
    throw error;
  }
};

/**
 * Update display configuration
 */
export const updateDisplayConfig = async (id: string, configData: Partial<DisplayData>): Promise<DisplayData> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.put<DisplayData>(`/account/displays/${id}/config`, configData);
    return response.data;
  } catch (error) {
    console.error(`Error updating display ${id} configuration:`, error);
    throw error;
  }
};

/**
 * Activate a display
 */
export const activateDisplay = async (id: string, replaceDisplayId?: string): Promise<DisplayData> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const payload = replaceDisplayId ? { replace_display_id: replaceDisplayId } : {};
    const response = await apiClient.post<{ success: boolean; data: DisplayData }>(`/account/displays/${id}/activate`, payload);
    return response.data.data; // Extract the nested data property
  } catch (error) {
    console.error(`Error activating display ${id}:`, error);
    throw error;
  }
};

/**
 * Deactivate a display
 */
export const deactivateDisplay = async (id: string): Promise<DisplayData> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.post<{ success: boolean; data: DisplayData }>(`/account/displays/${id}/deactivate`);
    return response.data.data; // Extract the nested data property
  } catch (error) {
    console.error(`Error deactivating display ${id}:`, error);
    throw error;
  }
};

/**
 * Get notes for a specific display
 */
export const fetchDisplayNotes = async (id: string): Promise<DisplayNote[]> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.get<DisplayNotesResponse>(`/account/displays/${id}/notes`);
    return response.data.notes;
  } catch (error) {
    console.error(`Error fetching notes for display ${id}:`, error);
    throw error;
  }
};

/**
 * Add notes to a display
 * @param id Display ID
 * @param noteIds Array of note IDs to add to the display
 * @param order Optional parameter for note order (1-based index)
 */
export const addNotesToDisplay = async (
  id: string, 
  noteIds: string[], 
  order?: Record<string, number>
): Promise<DisplayNotesResponse> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.post<DisplayNotesResponse>(
      `/account/displays/${id}/notes`,
      { noteIds, order }
    );
    return response.data;
  } catch (error) {
    console.error(`Error adding notes to display ${id}:`, error);
    throw error;
  }
};

/**
 * Remove a note from a display
 */
export const removeNoteFromDisplay = async (displayId: string, noteId: string): Promise<DisplayNotesResponse> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.delete<DisplayNotesResponse>(
      `/account/displays/${displayId}/notes/${noteId}`
    );
    return response.data;
  } catch (error) {
    console.error(`Error removing note ${noteId} from display ${displayId}:`, error);
    throw error;
  }
};

/**
 * Update notes order in a display
 */
export const updateNotesOrder = async (
  id: string, 
  noteOrder: Record<string, number>
): Promise<DisplayNotesResponse> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.put<DisplayNotesResponse>(
      `/account/displays/${id}/notes/order`,
      { order: noteOrder }
    );
    return response.data;
  } catch (error) {
    console.error(`Error updating note order for display ${id}:`, error);
    throw error;
  }
};

/**
 * Get all sessions for a display
 */
export const fetchDisplaySessions = async (
  displayId: string, 
  activeOnly: boolean = false,
  page: number = 1,
  limit: number = 10
): Promise<{ sessions: DisplaySession[], total: number }> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.get<SessionsResponse>(
      `/account/displays/${displayId}/sessions`, 
      { params: { active: activeOnly, page, limit } }
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching sessions for display ${displayId}:`, error);
    throw error;
  }
};

/**
 * End a specific session
 */
export const endDisplaySession = async (
  displayId: string, 
  sessionId: string
): Promise<DisplaySession> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.post<SessionResponse>(
      `/account/displays/${displayId}/sessions/${sessionId}/end`
    );
    return response.data.session;
  } catch (error) {
    console.error(`Error ending session ${sessionId} for display ${displayId}:`, error);
    throw error;
  }
};

/**
 * Get analytics for a specific session
 */
export const getSessionAnalytics = async (
  displayId: string,
  sessionId: string
): Promise<any> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.get(
      `/account/displays/${displayId}/sessions/${sessionId}/analytics`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching session analytics for session ${sessionId}:`, error);
    throw error;
  }
};

/**
 * Get display analytics for a given period
 */
export const getDisplayAnalytics = async (
  displayId: string,
  period: 'day' | 'week' | 'month' | 'year' = 'week',
  includeGeo: boolean = false
): Promise<DisplayAnalyticsData> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.get<DisplayAnalyticsData>(
      `/account/displays/${displayId}/analytics`,
      { params: { period, includeGeo } }
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching display analytics for display ${displayId}:`, error);
    throw error;
  }
};

/**
 * Export display analytics as CSV/Excel
 */
export const exportDisplayAnalytics = async (
  displayId: string,
  period: 'day' | 'week' | 'month' | 'year' = 'month'
): Promise<Blob> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    const response = await apiClient.get(
      `/account/displays/${displayId}/analytics/export`,
      { 
        params: { period },
        responseType: 'blob' // Important for file downloads
      }
    );
    return response.data as Blob;
  } catch (error) {
    console.error(`Error exporting display analytics for display ${displayId}:`, error);
    throw error;
  }
};

/**
 * Delete a display (admin/owner only)
 */
export const deleteDisplay = async (id: string): Promise<void> => {
  try {
    // Set authentication header before making the request
    setAuthHeader(apiClient);
    await apiClient.delete(`/account/displays/${id}`);
  } catch (error) {
    console.error(`Error deleting display ${id}:`, error);
    throw error;
  }
}; 